import axios from 'axios'
import { queuedMutation } from './offlineQueue'

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  // Send the HttpOnly auth cookie on every request. Needed for the
  // cookie-based JWT flow introduced in H1.
  withCredentials: true,
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't yank the user to /login mid background-sync. Offline-queue replay
      // requests carry X-Offline-Sync; their 401 (e.g. an expired cookie after a
      // long offline stint) is handled by the queue — the item stays pending and
      // flushes after the user re-authenticates (AuthContext + offlineQueue).
      // A 401 on normal traffic still redirects. axios 1.x headers is an
      // AxiosHeaders instance, so read it defensively.
      const h = error.config?.headers
      const isSync = !!h && (typeof h.get === 'function'
        ? h.get('X-Offline-Sync') === '1'
        : h['X-Offline-Sync'] === '1')
      // Only redirect if not already on login page (avoids redirect loop)
      if (!isSync && window.location.pathname !== '/login') {
        clearLocalSession()
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Auth
export const login = (email, password) =>
  api.post('/auth/login', { email, password }).then(res => res.data)

export const logout = () =>
  api.post('/auth/logout').then(res => res.data)

// What a device forgets when a session ends, whichever way it ends (Sign Out
// in AuthContext.logout, or the interceptor's 401 redirect): the cached
// identity the offline fallback would otherwise resurrect, and the active
// ranch, so the next person on a shared tablet does not inherit either.
// 'ranchId' is a legacy key older builds wrote; field devices may still carry it.
export function clearLocalSession() {
  for (const key of ['wb_last_me', 'currentRanchId', 'ranchId']) {
    try { localStorage.removeItem(key) } catch { /* localStorage unavailable (private mode) */ }
  }
}

export const getMe = () =>
  api.get('/auth/me').then(res => res.data)

// M3: AuthContext calls this periodically to rotate the HttpOnly cookie
// before its 15-minute TTL expires. Returns the fresh token payload so
// callers can re-read expires_in if they ever need to schedule dynamically.
export const refreshAuth = () =>
  api.post('/auth/refresh').then(res => res.data)

// -----------------------------------------------------------------------------
// ASSISTANT (in-app owner agent — Q&A chat + weekly digest viewing)
// Same agent core as the WhatsApp path; finance answers gated server-side by
// the caller's ranch role. Digest endpoints are owner-gated (403 otherwise).
// -----------------------------------------------------------------------------
export const askAssistant = (ranchId, question, language) =>
  api.post('/assistant/ask', { ranch_id: ranchId, question, language }).then(res => res.data)

// Voice Q&A: spoken question (en/lg/nyn) → transcript + answer.
export const askAssistantVoice = (ranchId, audioBlob, language) => {
  const form = new FormData()
  form.append('ranch_id', ranchId)
  if (language) form.append('language', language)
  form.append('audio', audioBlob, 'question.webm')
  return api.post('/assistant/ask/voice', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)
}

// -----------------------------------------------------------------------------
// WEEKLY TUESDAY REPORT (owner-gated) — full digest facts + multi-period rollups
// -----------------------------------------------------------------------------
export const getWeeklyReports = (ranchId, { page = 1, pageSize = 20 } = {}) =>
  api.get('/reports/digests', {
    params: { ranch_id: ranchId, page, page_size: pageSize, scope: 'ranch' },
  }).then(res => res.data)

export const getWeeklyReport = (digestId) =>
  api.get(`/reports/digests/${digestId}`).then(res => res.data)

export const getWeeklyRollup = (ranchId, granularity = 'month') =>
  api.get('/reports/weekly-rollup', { params: { ranch_id: ranchId, granularity } })
    .then(res => res.data)

// Weekly data-collection scorecards (streak strip) + monthly incentive
// statement — VIEW_REPORTS gated (manager and owner both see them).
export const getScorecards = (ranchId, limit = 12) =>
  api.get('/reports/scorecards', { params: { ranch_id: ranchId, limit } })
    .then(res => res.data)

export const getIncentiveStatement = (ranchId, month) =>
  api.get('/reports/incentive-statement', { params: { ranch_id: ranchId, month } })
    .then(res => res.data)

// Cash accounts + Monday balance entries (S5 balance check).
export const getCashAccounts = (ranchId) =>
  api.get('/finance/cash-accounts', { params: { ranch_id: ranchId } }).then(res => res.data)

export const createCashAccount = (payload) =>
  api.post('/finance/cash-accounts', payload).then(res => res.data)

export const upsertBalanceEntry = (payload) =>
  api.post('/finance/balance-entries', payload).then(res => res.data)

export const getBalanceCheck = (ranchId, weekStart) =>
  api.get('/finance/balance-check', {
    params: { ranch_id: ranchId, ...(weekStart ? { week_start: weekStart } : {}) },
  }).then(res => res.data)

// Owner operating-float (mig 118): deposits owners send to the manager's
// float, per-owner prepaid balances, and the quarterly allocation ratio.
export const createFloatDeposit = (payload) =>
  api.post('/finance/float-deposits', payload).then(res => res.data)

export const getFloatDeposits = (ranchId, limit = 50) =>
  api.get('/finance/float-deposits', { params: { ranch_id: ranchId, limit } }).then(res => res.data)

export const deleteFloatDeposit = (depositId) =>
  api.delete(`/finance/float-deposits/${depositId}`)

export const getFloatSummary = (ranchId) =>
  api.get('/finance/float-summary', { params: { ranch_id: ranchId } }).then(res => res.data)

// Streaming Q&A over SSE (native fetch + ReadableStream — no extra deps). Calls
// onTool(name) when a lookup runs, onToken(text) for each answer delta, and
// onError(text) for a graceful in-stream fallback. Throws on transport failure
// so the caller can fall back to the non-streaming askAssistant.
export const askAssistantStream = async (ranchId, question, language, { onTool, onToken, onError } = {}) => {
  const resp = await fetch('/api/v1/assistant/ask/stream', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ranch_id: ranchId, question, language }),
  })
  if (!resp.ok || !resp.body || typeof resp.body.getReader !== 'function') {
    throw new Error(`stream unavailable: ${resp.status}`)
  }
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let idx
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, idx)
      buffer = buffer.slice(idx + 2)
      const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '))
      if (!dataLine) continue
      let ev
      try { ev = JSON.parse(dataLine.slice(6)) } catch { continue }
      if (ev.type === 'tool') onTool?.(ev.name)
      else if (ev.type === 'token') onToken?.(ev.text)
      else if (ev.type === 'error') onError?.(ev.text)
    }
  }
}

// Record-over-web (confirm-then-commit). `recordAssistant` interprets free/rigid
// text; if it returns status 'awaiting_confirmation', call `confirmAssistant` with
// 'yes'/'no' (or a free-text correction). Returns the structured result dict.
export const recordAssistant = (ranchId, text) =>
  api.post('/assistant/record', { ranch_id: ranchId, text }).then(res => res.data)

export const confirmAssistant = (ranchId, reply) =>
  api.post('/assistant/record/confirm', { ranch_id: ranchId, reply }).then(res => res.data)

// Voice note → transcript → same confirm-then-commit flow. Returns the record
// result plus `transcript`. Transcribed via Google Cloud Speech-to-Text on the
// server (runtime SA credentials; graceful fallback when unavailable).
export const recordVoice = (ranchId, audioBlob, filename = 'voice.webm') => {
  const formData = new FormData()
  formData.append('ranch_id', ranchId)
  formData.append('audio', audioBlob, filename)
  return api.post('/assistant/record/voice', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)
}

// Attach a photo as evidence to a committed event (e.g. a death record).
export const recordPhoto = (ranchId, eventId, imageFile) => {
  const formData = new FormData()
  formData.append('ranch_id', ranchId)
  formData.append('event_id', eventId)
  formData.append('image', imageFile)
  return api.post('/assistant/record/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)
}

// Dashboard. The optional { signal } param lets React Query cancel the
// request when the component unmounts or the query key changes, preventing
// setState-on-unmounted-component warnings and saving bandwidth on flaky
// field links. Old callers that don't pass signal still work.
export const getDashboard = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}`, { signal }).then(res => res.data)

export const getQuickStats = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}/quick-stats`, { signal }).then(res => res.data)

export const getDashboardMetrics = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}/metrics`, { signal }).then(res => res.data)

// Fleet Assurance (system-admin only): every ranch ranked by trust score /
// open-exception backlog. Backend 403s non-admins.
export const getFleetAssurance = ({ signal } = {}) =>
  api.get('/assurance/fleet', { signal }).then(res => res.data)

// Drug / vaccine inventory (migration 110). ranch_id is a PATH segment.
export const getInventoryStock = (ranchId) =>
  api.get(`/inventory/${ranchId}/stock`).then(res => res.data)
export const getInventoryProducts = (ranchId) =>
  api.get(`/inventory/${ranchId}/products`).then(res => res.data)
export const createInventoryProduct = (ranchId, body) =>
  api.post(`/inventory/${ranchId}/products`, body).then(res => res.data)
export const recordInventoryReceipt = (ranchId, body) =>
  api.post(`/inventory/${ranchId}/receipts`, body).then(res => res.data)
export const recordInventoryAdjustment = (ranchId, body) =>
  api.post(`/inventory/${ranchId}/adjustments`, body).then(res => res.data)
export const recordInventoryCount = (ranchId, body) =>
  api.post(`/inventory/${ranchId}/counts`, body).then(res => res.data)
export const reconcileInventory = (ranchId) =>
  api.post(`/inventory/${ranchId}/reconcile`).then(res => res.data)

export const getDashboardAnomalies = (ranchId, { signal, grouped = false } = {}) =>
  api.get(`/dashboard/${ranchId}/anomalies`, {
    params: grouped ? { grouped: true } : undefined,
    signal,
  }).then(res => res.data)

export const getLatestReport = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}/latest-report`, { signal }).then(res => res.data)

// Owner-view plan, Session 2 — activity depth endpoints.
// All three accept { signal } for React Query cancellation + a period/days
// selector. Defaults match the backend defaults so callers can omit them.
export const getActivityPulse = (ranchId, { days = 7, signal } = {}) =>
  api.get(`/dashboard/${ranchId}/activity-pulse`, { params: { days }, signal }).then(res => res.data)

export const getHealthInterventions = (ranchId, { period = 'month', signal } = {}) =>
  api.get(`/dashboard/${ranchId}/health-interventions`, { params: { period }, signal }).then(res => res.data)

export const getWeighingTrend = (ranchId, { days = 90, signal } = {}) =>
  api.get(`/dashboard/${ranchId}/weighing-trend`, { params: { days }, signal }).then(res => res.data)

// Owner-view plan, Session 3 — paddock vegetation + grazing-current.
export const getPaddockHealth = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}/paddock-health`, { signal }).then(res => res.data)

export const getGrazingCurrent = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}/grazing-current`, { signal }).then(res => res.data)

// Grazing-intelligence Phase 8a — owner glance-view (grazing tracker) and
// Phase 8b — manager-log-vs-satellite verification matrix.
export const getGrazingTracker = (ranchId, { signal } = {}) =>
  api.get(`/dashboard/${ranchId}/grazing-tracker`, { signal }).then(res => res.data)

// Grazing verification MAP — 2-week satellite verdict vs calendar, scrubbable
// back through consecutive 2-week periods. Replaces the paddock×week heatmap.
export const getGrazingVerificationMap = (ranchId, { periods = 13, signal } = {}) =>
  api.get(`/dashboard/${ranchId}/grazing-verification-map`, { params: { periods }, signal })
    .then(res => res.data)

// One paddock's grazing events (materialised from the locked calendar + legacy
// flow) — its grazing history for the detail page.
export const getPaddockGrazingEvents = (paddockId, { limit = 30, signal } = {}) =>
  api.get(`/paddocks/${paddockId}/grazing`, { params: { limit }, signal }).then(res => res.data)

// -----------------------------------------------------------------------------
// RANCH MANAGEMENT APIs
// -----------------------------------------------------------------------------

export const getRanches = () => 
  api.get('/ranches').then(res => res.data)

export const getRanch = (ranchId) => 
  api.get(`/ranches/${ranchId}`).then(res => res.data)

export const createRanch = (data) => 
  api.post('/ranches', data).then(res => res.data)

export const updateRanch = (ranchId, data) => 
  api.patch(`/ranches/${ranchId}`, data).then(res => res.data)

export const deleteRanch = (ranchId) =>
  api.delete(`/ranches/${ranchId}`)

export const getOperatingProfile = (ranchId) =>
  api.get(`/ranches/${ranchId}/operating-profile`).then(res => res.data)

export const updateOperatingProfile = (ranchId, data) =>
  api.put(`/ranches/${ranchId}/operating-profile`, data).then(res => res.data)

// Cattle
export const getCattle = (ranchId, params = {}, { signal } = {}) =>
  api.get('/cattle', { params: { ranch_id: ranchId, ...params }, signal }).then(res => res.data)

// Fetch EVERY matching animal across pages. The list endpoint caps `limit` at
// 500, so a single getCattle() silently drops animals past the first 500 —
// wrong for any "act on / select from the whole herd" flow (bulk sessions,
// dam/sire pickers, dairy cohort lists). Returns a flat array of items.
export const getAllCattle = async (ranchId, params = {}, { signal } = {}) => {
  const pageSize = 500
  let offset = 0
  let all = []
  let total = Infinity
  while (offset < total) {
    const res = await getCattle(ranchId, { ...params, limit: pageSize, offset }, { signal })
    const items = res.items || res || []
    all = all.concat(items)
    total = res.total ?? all.length
    if (items.length < pageSize) break
    offset += pageSize
  }
  return all
}

export const getAnimal = (id, { signal } = {}) =>
  api.get(`/cattle/${id}`, { signal }).then(res => res.data)

export const createAnimal = (data) => 
  queuedMutation(api, { method: 'POST', url: '/cattle', data, label: 'Animal registration' })

export const updateAnimal = (id, data) => 
  api.patch(`/cattle/${id}`, data).then(res => res.data)

export const deleteAnimal = (id) => 
  api.delete(`/cattle/${id}`)

export const getAnimalEvents = (animalId) => 
  api.get(`/cattle/${animalId}/events`).then(res => res.data)

// The animal's days at the kraal (smartkraal.sightings, mig 138; plan
// smartkraal-owner-experience A2): newest first, each with the day's weighing and
// its change against the previous weighed day, the reads and every media variant.
export const getAnimalSightings = (animalId, params = {}, { signal } = {}) =>
  api.get(`/cattle/${animalId}/sightings`, { params, signal }).then(res => res.data)

// The kraal feed (plan smartkraal-owner-experience B2): kraal days newest first,
// each with the ranch-wide coverage, the caller's own coverage when their account
// is linked to an owner segment, and a strip of the animals seen that day; births
// and exits in the window. params: { days, segment, limit }.
export const getKraalFeed = (ranchId, params = {}, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/feed`, { params, signal }).then(res => res.data)

export const createCattleEvent = (data) => 
  queuedMutation(api, { method: 'POST', url: '/cattle/events', data, label: 'Cattle event' })

// Kraal census (Session 4 — offline-first capture). Wrapped in queuedMutation so
// it queues when offline and replays idempotently on reconnect.
export const createKraalObservation = (data) =>
  queuedMutation(api, { method: 'POST', url: '/census/observations', data, label: 'Kraal census' })

export const getHerdSummary = (ranchId, { signal } = {}) =>
  api.get(`/cattle/summary/${ranchId}`, { signal }).then(res => res.data)

// Event Verification (Manager Approval)
export const getPendingEvents = (ranchId, eventType = null) =>
  api.get('/cattle/events/pending', { params: { ranch_id: ranchId, event_type: eventType } }).then(res => res.data)

export const getPendingEventsCount = (ranchId) =>
  api.get('/cattle/events/pending/count', { params: { ranch_id: ranchId } }).then(res => res.data)

export const verifyEvent = (eventId, verifiedBy, notes = null) =>
  api.patch(`/cattle/events/${eventId}/verify`, { verified_by: verifiedBy, notes }).then(res => res.data)

export const rejectEvent = (eventId, verifiedBy, notes) =>
  api.patch(`/cattle/events/${eventId}/reject`, { verified_by: verifiedBy, notes }).then(res => res.data)

// Bulk-approve pending cattle events. Pass either { eventIds } (the events the
// user selected) or { verifyAll: true, eventType } to clear the whole filtered
// queue. Side-effect types (death/sale/calving/...) are skipped server-side
// and returned in skipped_side_effect.
export const verifyEventsBulk = (ranchId, { eventIds = null, eventType = null, verifyAll = false, notes = null } = {}) =>
  api.post('/cattle/events/verify-bulk', {
    ranch_id: ranchId,
    event_ids: eventIds,
    event_type: eventType,
    verify_all: verifyAll,
    notes,
  }).then(res => res.data)

// Approvals Inbox (Session 3 of roles-permissions overhaul)
// Unified queue of pending cattle events + finance expense/income requests.
export const getApprovalsInbox = (ranchId = null) =>
  api.get('/approvals/inbox', { params: ranchId ? { ranch_id: ranchId } : {} }).then(res => res.data)

export const getApprovalsInboxCount = (ranchId = null) =>
  api.get('/approvals/inbox/count', { params: ranchId ? { ranch_id: ranchId } : {} }).then(res => res.data)

export const getApprovalRequest = (requestId) =>
  api.get(`/approvals/requests/${requestId}`).then(res => res.data)

// Finance approval settings (mig 117): approval policy + designated approvers.
export const getFinanceApprovalSettings = (ranchId) =>
  api.get('/approvals/finance-settings', { params: { ranch_id: ranchId } }).then(res => res.data)

export const updateFinanceApprovalSettings = (ranchId, { policy = null, designatedApproverUserIds = null } = {}) =>
  api.put('/approvals/finance-settings', {
    ranch_id: ranchId,
    policy,
    designated_approver_user_ids: designatedApproverUserIds,
  }).then(res => res.data)

export const approveExpense = (expenseId, notes = null) =>
  api.post(`/finance/expenses/${expenseId}/approve`, { notes }).then(res => res.data)

export const rejectExpense = (expenseId, notes = null) =>
  api.post(`/finance/expenses/${expenseId}/reject`, { notes }).then(res => res.data)

export const approveIncome = (incomeId, notes = null) =>
  api.post(`/finance/income/${incomeId}/approve`, { notes }).then(res => res.data)

export const rejectIncome = (incomeId, notes = null) =>
  api.post(`/finance/income/${incomeId}/reject`, { notes }).then(res => res.data)

// Session 4: system_admin tier-2 approval vote on a pending
// system_admin_action request. Cattle + finance vote through their own
// domain-specific endpoints; this generic one is only for admin-initiated
// destructive actions (e.g. a ranch delete that needs peer quorum).
export const voteOnApprovalRequest = (requestId, decision, notes = null) =>
  api.post(`/approvals/requests/${requestId}/vote`, { decision, notes }).then(res => res.data)

// RFID Tag Lookup
export const lookupByRfid = (rfidTag, ranchId) =>
  api.get(`/cattle/lookup/${encodeURIComponent(rfidTag)}`, { params: { ranch_id: ranchId } }).then(res => res.data)

export const checkRfidExists = (rfidTag, ranchId) =>
  api.get(`/cattle/check-rfid/${encodeURIComponent(rfidTag)}`, { params: { ranch_id: ranchId } }).then(res => res.data)

// Weigh Sessions (Tru-Test CSV Import)
export const importWeighSession = (data) =>
  api.post('/cattle/weigh-sessions', data).then(res => res.data)

export const importPregnancyCampaign = (formData) =>
  api.post('/dairy/pregnancy-campaigns', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)

export const getPregnancyCampaigns = (ranchId, params = {}) =>
  api.get('/dairy/pregnancy-campaigns', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getWeighSessions = (ranchId, params = {}) =>
  api.get('/cattle/weigh-sessions', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

// Bulk Treatment/Vaccination Sessions
export const createTreatmentSession = (data) =>
  queuedMutation(api, { method: 'POST', url: '/cattle/treatment-sessions', data, label: 'Treatment session' })

export const getTreatmentSessions = (ranchId, params = {}) =>
  api.get('/cattle/treatment-sessions', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

// Animal Photos (Crush Camera - Server-side Folder Scan)
export const previewPhotoFolder = (folderPath = null) =>
  api.post('/cattle/photos/preview-folder', null, {
    params: folderPath ? { folder_path: folderPath } : {},
  }).then(res => res.data)

export const scanPhotoFolder = (data) =>
  api.post('/cattle/photos/scan', data).then(res => res.data)

export const uploadPhotoSession = (formData) =>
  api.post('/cattle/photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)

export const getAnimalPhotos = (ranchId, params = {}) =>
  api.get('/cattle/photos', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

// Paddocks
export const getPaddocks = (ranchId, params = {}) => 
  api.get('/paddocks', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getPaddock = (id) => 
  api.get(`/paddocks/${id}`).then(res => res.data)

export const createPaddock = (data) => 
  api.post('/paddocks', data).then(res => res.data)

export const updatePaddock = (id, data) => 
  api.patch(`/paddocks/${id}`, data).then(res => res.data)

// RENAMED: Original paddock grazing functions (legacy)
export const startPaddockGrazing = (data) => 
  api.post('/paddocks/grazing', data).then(res => res.data)

export const endPaddockGrazing = (eventId, data) => 
  api.patch(`/paddocks/grazing/${eventId}`, data).then(res => res.data)

export const getPaddockSummary = (ranchId) => 
  api.get(`/paddocks/summary/${ranchId}`).then(res => res.data)

// Paddock Configurations
export const getPaddockConfigurations = (ranchId) =>
  api.get('/paddocks/configurations', { params: { ranch_id: ranchId } }).then(res => res.data)

export const getConfigurationDetail = (configId) =>
  api.get(`/paddocks/configurations/${configId}`).then(res => res.data)

export const reconfigurePaddocks = (data) =>
  api.post('/paddocks/reconfigure', data).then(res => res.data)

// Lantana / Vegetation Patches
export const getLantanaPatches = (ranchId, params = {}) =>
  api.get('/paddocks/lantana', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)
export const getVegetationPatches = getLantanaPatches

export const createLantanaPatch = (data) =>
  api.post('/paddocks/lantana', data).then(res => res.data)

export const updateLantanaPatch = (id, data) =>
  api.patch(`/paddocks/lantana/${id}`, data).then(res => res.data)

export const bulkDetectPatches = (data) =>
  api.post('/paddocks/lantana/bulk-detect', data).then(res => res.data)

// Batch Planning
export const planBatchCreation = (ranchId, patchIds) =>
  api.post(`/biochar/batches/plan?ranch_id=${ranchId}`, patchIds).then(res => res.data)

export const bulkCreateBatches = (data) =>
  api.post('/biochar/batches/bulk-create', data).then(res => res.data)

// Ranch Biochar Settings
export const getBiocharSettings = (ranchId) =>
  api.get(`/biochar/settings/${ranchId}`).then(res => res.data)

export const updateBiocharSettings = (ranchId, settings) =>
  api.patch(`/biochar/settings/${ranchId}`, settings).then(res => res.data)

// Finance
export const getExpenseCategories = () => 
  api.get('/finance/categories').then(res => res.data)

export const getExpenses = (ranchId, params = {}) => 
  api.get('/finance/expenses', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createExpense = (data) => 
  api.post('/finance/expenses', data).then(res => res.data)

export const updateExpense = (id, data) => 
  api.patch(`/finance/expenses/${id}`, data).then(res => res.data)

export const deleteExpense = (id) =>
  api.delete(`/finance/expenses/${id}`)

// Owner-finance ledger (migration 105). includeEmpty=true returns ALL non-deleted
// segments (for the animal-registration dropdown); default filters to owners with
// animals/finance activity (for the finance selectors).
export const getOwnerSegments = (ranchId, { includeEmpty = false } = {}) =>
  api.get(`/cattle/owner-segments/${ranchId}`, {
    params: includeEmpty ? { include_empty: true } : undefined,
  }).then(res => res.data)

// Link an owner segment to an owner's account (userId null unlinks). The link is
// what scopes the owner's dashboard card and 'my cattle' (Phase 4a); MANAGE_USERS.
// `replace` moves a segment that another account holds (the UI confirms first).
export const linkOwnerSegmentUser = (segmentId, userId, { replace = false } = {}) =>
  api.patch(`/cattle/owner-segments/${segmentId}`, { user_id: userId, replace }).then(res => res.data)

export const getDirectorAccounts = (ranchId) =>
  api.get(`/finance/director-accounts/${ranchId}`).then(res => res.data)

export const getSettlements = (ranchId) =>
  api.get(`/finance/settlements/${ranchId}`).then(res => res.data)

export const createSettlement = (data) =>
  api.post('/finance/settlements', data).then(res => res.data)

export const getIncome = (ranchId, params = {}) => 
  api.get('/finance/income', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createIncome = (data) => 
  api.post('/finance/income', data).then(res => res.data)

export const getFinanceSummary = (ranchId, period = 'month') => 
  api.get(`/finance/summary/${ranchId}`, { params: { period } }).then(res => res.data)

export const getFinanceTrends = (ranchId, months = 6) =>
  api.get(`/finance/trends/${ranchId}`, { params: { months } }).then(res => res.data)

export const recordExpensePayment = (expenseId, data) =>
  api.post(`/finance/expenses/${expenseId}/record-payment`, data).then(res => res.data)

export const recordIncomePayment = (incomeId, data) =>
  api.post(`/finance/income/${incomeId}/record-payment`, data).then(res => res.data)

// Biochar
export const getBiocharBatches = (ranchId, params = {}) => 
  api.get('/biochar/batches', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getBiocharBatch = (id) => 
  api.get(`/biochar/batches/${id}`).then(res => res.data)

export const createBiocharBatch = (data) =>
  queuedMutation(api, { method: 'POST', url: '/biochar/batches', data, label: 'Biochar batch' })

export const updateBiocharBatch = (id, data) => 
  api.patch(`/biochar/batches/${id}`, data).then(res => res.data)

export const getBiocharSummary = (ranchId) => 
  api.get(`/biochar/summary/${ranchId}`).then(res => res.data)

// Breeds
export const getBreeds = () => 
  api.get('/breeds').then(res => res.data)

// Agent Framework
export const getAgentDashboard = (ranchId) => 
  api.get(`/agents/dashboard/${ranchId}`).then(res => res.data)

export const getAgentStatus = (ranchId) => 
  api.get(`/agents/status/${ranchId}`).then(res => res.data)

export const getAgentRuns = (ranchId, params = {}) => 
  api.get(`/agents/runs/${ranchId}`, { params }).then(res => res.data)

export const triggerAgent = (ranchId, agentName, force = false) => 
  api.post(`/agents/run/${ranchId}/${agentName}`, { force }).then(res => res.data)

// Discrepancy Flags
export const getDiscrepancies = (ranchId, status = 'open') => 
  api.get(`/agents/discrepancies/${ranchId}`, { params: { status } }).then(res => res.data)

export const resolveDiscrepancy = (flagId, notes, resolvedBy) => 
  api.patch(`/agents/discrepancies/${flagId}/resolve`, { resolution_notes: notes, resolved_by: resolvedBy }).then(res => res.data)

export const dismissDiscrepancy = (flagId) => 
  api.patch(`/agents/discrepancies/${flagId}/dismiss`).then(res => res.data)

// Agent Alerts
export const getAgentAlerts = (ranchId, status = 'pending') => 
  api.get(`/agents/alerts/${ranchId}`, { params: { status } }).then(res => res.data)

export const dismissAlert = (alertId) => 
  api.patch(`/agents/alerts/${alertId}/dismiss`).then(res => res.data)

// Grazing Recommendations
export const getGrazingRecommendations = (ranchId) => 
  api.get(`/agents/recommendations/grazing/${ranchId}`).then(res => res.data)

export const acceptGrazingRecommendation = (recId) => 
  api.post(`/agents/recommendations/grazing/${recId}/accept`).then(res => res.data)

// -----------------------------------------------------------------------------
// REFERENCE DATA APIs
// -----------------------------------------------------------------------------

export const getVaccines = () => 
  api.get('/reference/vaccines').then(res => res.data)

export const getTreatments = (treatmentType = null) => 
  api.get('/reference/treatments', { params: { treatment_type: treatmentType } }).then(res => res.data)

export const getDeathCauses = () => 
  api.get('/reference/death-causes').then(res => res.data)

export const getPayees = (ranchId) => 
  api.get('/reference/payees', { params: { ranch_id: ranchId } }).then(res => res.data)

export const createPayee = (data) => 
  api.post('/reference/payees', data).then(res => res.data)

export const getHerds = () => 
  api.get('/reference/herds').then(res => res.data)

export const getAnimalStatuses = () => 
  api.get('/reference/animal-statuses').then(res => res.data)

export const getPaymentMethods = () => 
  api.get('/reference/payment-methods').then(res => res.data)

export const getIncomeTypes = () => 
  api.get('/reference/income-types').then(res => res.data)

// -----------------------------------------------------------------------------
// USER MANAGEMENT APIs
// -----------------------------------------------------------------------------

export const getUsers = (ranchId, params = {}) => 
  api.get('/users', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getUser = (userId, ranchId) => 
  api.get(`/users/${userId}`, { params: { ranch_id: ranchId } }).then(res => res.data)

export const getCurrentUser = (userId, ranchId) => 
  api.get('/users/me', { params: { user_id: userId, ranch_id: ranchId } }).then(res => res.data)

export const createUser = (data, requestingUserId) => 
  api.post('/users', data, { params: { requesting_user_id: requestingUserId } }).then(res => res.data)

export const updateUser = (userId, data, ranchId, requestingUserId) => 
  api.patch(`/users/${userId}`, data, { 
    params: { ranch_id: ranchId, requesting_user_id: requestingUserId } 
  }).then(res => res.data)

export const updateUserRole = (userId, ranchId, newRole, requestingUserId) => 
  api.patch(`/users/${userId}/role`, null, { 
    params: { ranch_id: ranchId, new_role: newRole, requesting_user_id: requestingUserId } 
  }).then(res => res.data)

export const deactivateUser = (userId, ranchId, requestingUserId) => 
  api.delete(`/users/${userId}`, { 
    params: { ranch_id: ranchId, requesting_user_id: requestingUserId } 
  }).then(res => res.data)

export const getRoles = () => 
  api.get('/users/roles/list').then(res => res.data)

// -----------------------------------------------------------------------------
// NOTIFICATION APIs
// -----------------------------------------------------------------------------

export const getNotifications = (ranchId, userId = null, unreadOnly = false, limit = 20, offset = 0) =>
  api.get('/notifications', {
    params: { ranch_id: ranchId, user_id: userId, unread_only: unreadOnly, limit, offset }
  }).then(res => res.data)

export const getNotificationCount = (ranchId, userId = null) => 
  api.get('/notifications/count', { 
    params: { ranch_id: ranchId, user_id: userId } 
  }).then(res => res.data)

export const markNotificationRead = (notificationId) => 
  api.patch(`/notifications/${notificationId}/read`).then(res => res.data)

export const markAllNotificationsRead = (ranchId, userId = null) => 
  api.patch('/notifications/read-all', null, { 
    params: { ranch_id: ranchId, user_id: userId } 
  }).then(res => res.data)

export const deleteNotification = (notificationId) => 
  api.delete(`/notifications/${notificationId}`).then(res => res.data)

// -----------------------------------------------------------------------------
// GRAZING SCHEDULE APIs
// -----------------------------------------------------------------------------

export const getGrazingSchedule = (ranchId, params = {}) => 
  api.get('/grazing/schedule', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getGrazingScheduleEntry = (scheduleId) => 
  api.get(`/grazing/schedule/${scheduleId}`).then(res => res.data)

export const createGrazingSchedule = (data) => 
  queuedMutation(api, { method: 'POST', url: '/grazing/schedule', data, label: 'Grazing schedule' })

export const updateGrazingSchedule = (scheduleId, data) => 
  api.patch(`/grazing/schedule/${scheduleId}`, data).then(res => res.data)

export const cancelGrazingSchedule = (scheduleId) => 
  api.delete(`/grazing/schedule/${scheduleId}`).then(res => res.data)

export const startGrazing = (scheduleId, actualEntryDate = null) => 
  api.post(`/grazing/schedule/${scheduleId}/start`, null, { 
    params: { actual_entry_date: actualEntryDate } 
  }).then(res => res.data)

export const endGrazing = (scheduleId, actualExitDate = null) => 
  api.post(`/grazing/schedule/${scheduleId}/end`, null, { 
    params: { actual_exit_date: actualExitDate } 
  }).then(res => res.data)

export const getGrazingAlerts = (ranchId) => 
  api.get('/grazing/alerts', { params: { ranch_id: ranchId } }).then(res => res.data)

export const getHerdLocations = (ranchId) => 
  api.get('/grazing/herd-locations', { params: { ranch_id: ranchId } }).then(res => res.data)

// -----------------------------------------------------------------------------
// LANTANA SCHEDULE APIs
// -----------------------------------------------------------------------------

export const getLantanaClearingSchedule = (ranchId, params = {}) => 
  api.get('/lantana-schedule', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const scheduleLantanaClearing = (data) => 
  api.post('/lantana-schedule', data).then(res => res.data)

export const executeLantanaClearing = (scheduleId, executedBy = null, executedDate = null) => 
  api.post(`/lantana-schedule/${scheduleId}/execute`, null, { 
    params: { executed_by: executedBy, executed_date: executedDate } 
  }).then(res => res.data)

export const cancelLantanaClearing = (scheduleId) =>
  api.delete(`/lantana-schedule/${scheduleId}`).then(res => res.data)

export const assignLantanaClearing = (scheduleId, assignedTo) =>
  api.post(`/lantana-schedule/${scheduleId}/assign`, { assigned_to: assignedTo }).then(res => res.data)

export const startLantanaClearing = (scheduleId) =>
  api.post(`/lantana-schedule/${scheduleId}/start`).then(res => res.data)

export const assignBiocharBatch = (batchId, operatorId) =>
  api.post(`/biochar/batches/${batchId}/assign`, { operator_id: operatorId }).then(res => res.data)

// -----------------------------------------------------------------------------
// WEEKLY GRAZING CALENDAR APIs
// -----------------------------------------------------------------------------

// Week Status
export const getGrazingWeeks = (ranchId, params = {}) =>
  api.get('/grazing/calendar/weeks', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getWeekStatus = (ranchId, weekStart) =>
  api.get(`/grazing/calendar/weeks/${weekStart}`, { params: { ranch_id: ranchId } }).then(res => res.data)

export const initializeWeek = (ranchId, weekStart) =>
  api.post(`/grazing/calendar/weeks/${weekStart}/initialize`, null, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

export const lockWeek = (ranchId, weekStart, data) =>
  api.post(`/grazing/calendar/weeks/${weekStart}/lock`, data, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

export const unlockWeek = (ranchId, weekStart, data) =>
  api.post(`/grazing/calendar/weeks/${weekStart}/unlock`, data, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

// Herd Entries (Paddock Stays)
export const getWeekEntries = (ranchId, weekStart, herd = null) =>
  api.get(`/grazing/calendar/weeks/${weekStart}/entries`, {
    params: { ranch_id: ranchId, herd }
  }).then(res => res.data)

export const createHerdEntry = (ranchId, weekStart, data) =>
  api.post(`/grazing/calendar/weeks/${weekStart}/entries`, data, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

export const updateHerdEntry = (ranchId, weekStart, entryId, data) =>
  api.patch(`/grazing/calendar/weeks/${weekStart}/entries/${entryId}`, data, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

export const deleteHerdEntry = (ranchId, weekStart, entryId) =>
  api.delete(`/grazing/calendar/weeks/${weekStart}/entries/${entryId}`, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

// Legacy alias for backwards compatibility
export const saveHerdEntry = createHerdEntry

// Daily Notes
export const getWeekNotes = (ranchId, weekStart, herd = null) =>
  api.get(`/grazing/calendar/weeks/${weekStart}/notes`, {
    params: { ranch_id: ranchId, herd }
  }).then(res => res.data)

export const saveDailyNote = (ranchId, weekStart, data) =>
  api.post(`/grazing/calendar/weeks/${weekStart}/notes`, data, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

export const bulkSaveNotes = (ranchId, weekStart, notes) =>
  api.put(`/grazing/calendar/weeks/${weekStart}/notes/bulk`, { notes }, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

// Dynamic Herds
export const getActiveHerds = (ranchId) =>
  api.get('/grazing/calendar/herds', { params: { ranch_id: ranchId } }).then(res => res.data)

export const getHerdOpenEvents = (ranchId, herd) =>
  api.get(`/grazing/calendar/herds/${herd}/open-events`, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

// Open Event Management (for closing events that span multiple weeks)
export const closeOpenEvent = (ranchId, eventId, exitDate) =>
  api.patch(`/grazing/calendar/open-events/${eventId}/close`,
    { exit_date: exitDate },
    { params: { ranch_id: ranchId } }
  ).then(res => res.data)

export const reopenEvent = (ranchId, eventId) =>
  api.patch(`/grazing/calendar/open-events/${eventId}/reopen`, null, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

// Reminder Generation
export const generateGrazingReminders = (ranchId) =>
  api.post('/grazing/calendar/generate-reminders', null, {
    params: { ranch_id: ranchId }
  }).then(res => res.data)

// -----------------------------------------------------------------------------
// REMOTE SENSING APIs
// -----------------------------------------------------------------------------

export const createRemoteSensingSource = (data) =>
  api.post('/remote-sensing/sources', data).then(res => res.data)

export const getRemoteSensingSources = (ranchId, params = {}) =>
  api.get('/remote-sensing/sources', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const bulkCreateObservations = (data) =>
  api.post('/remote-sensing/observations/bulk', data).then(res => res.data)

export const getRemoteSensingObservations = (ranchId, params = {}) =>
  api.get('/remote-sensing/observations', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getRemoteSensingTimeseries = (paddockId, params = {}) =>
  api.get(`/remote-sensing/paddock/${paddockId}/timeseries`, { params }).then(res => res.data)

export const getRemoteSensingSnapshot = (ranchId) =>
  api.get(`/remote-sensing/ranch/${ranchId}/snapshot`).then(res => res.data)

export const getRemoteSensingTrends = (ranchId, params = {}) =>
  api.get(`/remote-sensing/ranch/${ranchId}/trends`, { params }).then(res => res.data)

// Patch-level remote sensing
export const bulkCreatePatchObservations = (data) =>
  api.post('/remote-sensing/observations/bulk-patches', data).then(res => res.data)

export const getPatchTimeseries = (patchId, params = {}) =>
  api.get(`/remote-sensing/patch/${patchId}/timeseries`, { params }).then(res => res.data)

export const getPatchesSnapshot = (ranchId, params = {}) =>
  api.get('/remote-sensing/patches/snapshot', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

// -----------------------------------------------------------------------------
// RANCH REPORT APIs
// -----------------------------------------------------------------------------

export const generateReport = (data) =>
  api.post('/reports/generate', data).then(res => res.data)

export const getReports = (ranchId, params = {}) =>
  api.get('/reports/', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getReport = (reportId) =>
  api.get(`/reports/${reportId}`).then(res => res.data)

export const updateReportSection = (reportId, sectionIndex, newContent) =>
  api.put(`/reports/${reportId}/sections/${sectionIndex}`, { new_content: newContent }).then(res => res.data)

export const approveReport = (reportId) =>
  api.post(`/reports/${reportId}/approve`).then(res => res.data)

export const archiveReport = (reportId) =>
  api.post(`/reports/${reportId}/archive`).then(res => res.data)

export const regenerateReport = (reportId) =>
  api.post(`/reports/${reportId}/regenerate`).then(res => res.data)

export const deleteReport = (reportId) =>
  api.delete(`/reports/${reportId}`).then(res => res.data)

// -----------------------------------------------------------------------------
// MARKET PRICES
// -----------------------------------------------------------------------------

export const getLatestPrices = (ranchId) =>
  api.get('/market-prices/latest', { params: { ranch_id: ranchId } }).then(res => res.data)

export const getPriceHistory = (ranchId, params = {}) =>
  api.get('/market-prices/history', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const recordPrice = (data) =>
  api.post('/market-prices/', data).then(res => res.data)

export const recordBulkPrices = (data) =>
  api.post('/market-prices/bulk', data).then(res => res.data)

export const deletePrice = (priceId) =>
  api.delete(`/market-prices/${priceId}`).then(res => res.data)

// -----------------------------------------------------------------------------
// BIOMASS GROUND TRUTH
// -----------------------------------------------------------------------------

export const getBiomassRecords = (ranchId, params = {}) =>
  api.get('/biomass/ground-truth', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const recordBiomass = (data) =>
  queuedMutation(api, { method: 'POST', url: '/biomass/ground-truth', data, label: 'Biomass sample' })

export const getBiomassModelStatus = (ranchId) =>
  api.get('/biomass/model-status', { params: { ranch_id: ranchId } }).then(res => res.data)

// -----------------------------------------------------------------------------
// MONITORING POINTS
// -----------------------------------------------------------------------------

export const getMonitoringPoints = (ranchId, params = {}) =>
  api.get('/monitoring-points/', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getMonitoringPoint = (pointId) =>
  api.get(`/monitoring-points/${pointId}`).then(res => res.data)

export const createMonitoringPoint = (data) =>
  api.post('/monitoring-points/', data).then(res => res.data)

export const updateMonitoringPoint = (pointId, data) =>
  api.put(`/monitoring-points/${pointId}`, data).then(res => res.data)

export const deleteMonitoringPoint = (pointId) =>
  api.delete(`/monitoring-points/${pointId}`).then(res => res.data)

export const uploadMonitoringPhoto = (pointId, formData) =>
  api.post(`/monitoring-points/${pointId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)

// -----------------------------------------------------------------------------
// CLASSIFICATION TRAINING DATA
// -----------------------------------------------------------------------------

export const getTrainingSamples = (ranchId, params = {}) =>
  api.get('/training-data/', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getTrainingSample = (sampleId) =>
  api.get(`/training-data/${sampleId}`).then(res => res.data)

export const createTrainingSample = (data) =>
  api.post('/training-data/', data).then(res => res.data)

export const linkSpectralData = (sampleId) =>
  api.post(`/training-data/${sampleId}/link-spectral`).then(res => res.data)

// -----------------------------------------------------------------------------
// FIELD PHOTO UPLOAD
// -----------------------------------------------------------------------------

export const uploadFieldPhoto = (file, context = 'general') => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('context', context)
  return api.post('/field-photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)
}

// -----------------------------------------------------------------------------
// UNIFIED MEDIA UPLOAD
// -----------------------------------------------------------------------------

export const uploadMedia = async (file, { context, ranch_id, entity_type, entity_id } = {}) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('context', context || 'general')
  if (ranch_id) formData.append('ranch_id', ranch_id)
  if (entity_type) formData.append('entity_type', entity_type)
  if (entity_id) formData.append('entity_id', entity_id)
  const { data } = await api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data // { id, url, storage_key, ... }
}

// -----------------------------------------------------------------------------
// DAIRY APIs
// -----------------------------------------------------------------------------

// Milk Sessions
export const importMilkSession = (data) =>
  api.post('/dairy/milk-sessions', data).then(res => res.data)

export const getMilkSessions = (ranchId, params = {}) =>
  api.get('/dairy/milk-sessions', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getMilkSession = (sessionId) =>
  api.get(`/dairy/milk-sessions/${sessionId}`).then(res => res.data)

// Milk Recordings
export const getMilkRecordings = (ranchId, params = {}) =>
  api.get('/dairy/milk-recordings', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getMilkRecordingSummary = (ranchId, params = {}) =>
  api.get('/dairy/milk-recordings/summary', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

// Quality Tests
export const createMilkQualityTest = (data) =>
  api.post('/dairy/quality-tests', data).then(res => res.data)

export const getMilkQualityTests = (ranchId, params = {}) =>
  api.get('/dairy/quality-tests', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const bulkImportQualityTests = (data) =>
  api.post('/dairy/quality-tests/bulk', data).then(res => res.data)

// Lactation Cycles
export const getLactationCycles = (ranchId, params = {}) =>
  api.get('/dairy/lactation-cycles', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createLactationCycle = (data) =>
  api.post('/dairy/lactation-cycles', data).then(res => res.data)

export const updateLactationCycle = (cycleId, data) =>
  api.patch(`/dairy/lactation-cycles/${cycleId}`, data).then(res => res.data)

export const getLactationDashboard = (ranchId) =>
  api.get('/dairy/lactation-cycles/dashboard', { params: { ranch_id: ranchId } }).then(res => res.data)

// Reproductive Events
export const createReproductiveEvent = (data) =>
  api.post('/dairy/reproductive-events', data).then(res => res.data)

export const getReproductiveEvents = (ranchId, params = {}) =>
  api.get('/dairy/reproductive-events', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

// Mastitis Cases
export const createMastitisCase = (data) =>
  api.post('/dairy/mastitis-cases', data).then(res => res.data)

export const getMastitisCases = (ranchId, params = {}) =>
  api.get('/dairy/mastitis-cases', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const updateMastitisCase = (caseId, data) =>
  api.patch(`/dairy/mastitis-cases/${caseId}`, data).then(res => res.data)

// Dairy Buyers
export const getDairyBuyers = (ranchId, params = {}) =>
  api.get('/dairy/buyers', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createDairyBuyer = (data) =>
  api.post('/dairy/buyers', data).then(res => res.data)

export const updateDairyBuyer = (buyerId, data) =>
  api.patch(`/dairy/buyers/${buyerId}`, data).then(res => res.data)

// Milk Collections
export const getMilkCollections = (ranchId, params = {}) =>
  api.get('/dairy/milk-collections', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createMilkCollection = (data) =>
  api.post('/dairy/milk-collections', data).then(res => res.data)

// Dairy Reference Data
export const getDairyMilkingMethods = () =>
  api.get('/reference/dairy-milking-methods').then(res => res.data)

export const getDairyQuarters = () =>
  api.get('/reference/dairy-quarters').then(res => res.data)

export const getDairyCmtScores = () =>
  api.get('/reference/dairy-cmt-scores').then(res => res.data)

export const getDairyDetectionMethods = () =>
  api.get('/reference/dairy-detection-methods').then(res => res.data)

export const getDairySireBreeds = () =>
  api.get('/reference/dairy-sire-breeds').then(res => res.data)

export const getDairyBuyerTypes = () =>
  api.get('/reference/dairy-buyer-types').then(res => res.data)

export const getDairyReproductiveEventTypes = () =>
  api.get('/reference/dairy-reproductive-events').then(res => res.data)

// -----------------------------------------------------------------------------
// SOIL SAMPLES (MRV)
// -----------------------------------------------------------------------------

export const getSoilSamples = (ranchId, params = {}) =>
  api.get('/soil/samples', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createSoilSample = (data) =>
  queuedMutation(api, { method: 'POST', url: '/soil/samples', data, label: 'Soil sample' })

export const updateSoilSample = (id, data) =>
  api.patch(`/soil/samples/${id}`, data).then(res => res.data)

export const deleteSoilSample = (id) =>
  api.delete(`/soil/samples/${id}`).then(res => res.data)

export const getSoilSummary = (ranchId) =>
  api.get('/soil/summary', { params: { ranch_id: ranchId } }).then(res => res.data)

// -----------------------------------------------------------------------------
// TARGET STATE & FARM TYPOLOGY (MRV)
// -----------------------------------------------------------------------------

export const getPaddockTargets = (paddockId) =>
  api.get(`/land/paddocks/${paddockId}/targets`).then(res => res.data)

export const updatePaddockTargets = (paddockId, data) =>
  api.put(`/land/paddocks/${paddockId}/targets`, data).then(res => res.data)

export const getFarmTypology = (ranchId) =>
  api.get('/land/farm-typology', { params: { ranch_id: ranchId } }).then(res => res.data)

export const upsertFarmTypology = (data) =>
  api.put('/land/farm-typology', data).then(res => res.data)

// -----------------------------------------------------------------------------
// WEIGHT TRAJECTORIES (MRV)
// -----------------------------------------------------------------------------

export const getWeightTrajectories = (ranchId, paddockId = null) =>
  api.get('/cattle/weight-trajectories', { params: { ranch_id: ranchId, ...(paddockId ? { paddock_id: paddockId } : {}) } }).then(res => res.data)

export const computeWeightTrajectories = (ranchId) =>
  api.post('/cattle/weight-trajectories/compute', null, { params: { ranch_id: ranchId } }).then(res => res.data)

// -----------------------------------------------------------------------------
// SPOT CHECKS (MRV audit independence)
// -----------------------------------------------------------------------------

export const getSpotChecks = (ranchId, params = {}) =>
  api.get('/spot-checks/', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createSpotCheck = (data) =>
  queuedMutation(api, { method: 'POST', url: '/spot-checks/', data, label: 'Spot check' })

export const generateSpotCheckPoints = (ranchId, count = 5, paddockId = null) =>
  api.post('/spot-checks/generate-points', null, { params: { ranch_id: ranchId, count, ...(paddockId ? { paddock_id: paddockId } : {}) } }).then(res => res.data)

export const getSpotCheckSummary = (ranchId) =>
  api.get('/spot-checks/summary', { params: { ranch_id: ranchId } }).then(res => res.data)

// -----------------------------------------------------------------------------
// BIOMASS MODELS (MRV)
// -----------------------------------------------------------------------------

export const trainBiomassModels = (ranchId) =>
  api.post('/biomass/train-models', null, { params: { ranch_id: ranchId } }).then(res => res.data)

export const predictBiomass = (ranchId, ndvi) =>
  api.get('/biomass/predict', { params: { ranch_id: ranchId, ndvi } }).then(res => res.data)

// -----------------------------------------------------------------------------
// DRONE RUNS (MRV)
// -----------------------------------------------------------------------------

export const getDroneRuns = (ranchId) =>
  api.get('/drone-runs', { params: { ranch_id: ranchId } }).then(res => res.data)

export const getDroneRun = (runId) =>
  api.get(`/drone-runs/${runId}`).then(res => res.data)

export const createDroneRun = (data) =>
  queuedMutation(api, { method: 'POST', url: '/drone-runs', data, label: 'Drone run' })

export const updateDroneRun = (runId, data) =>
  api.patch(`/drone-runs/${runId}`, data).then(res => res.data)

export const deleteDroneRun = (runId) =>
  api.delete(`/drone-runs/${runId}`).then(res => res.data)

// Flight verifications (S2 — biochar pilot land monitoring)
export const getFlightVerification = (runId) =>
  api.get(`/drone-runs/${runId}/verification`).then(res => res.data)

export const upsertFlightVerification = (runId, data) =>
  api.put(`/drone-runs/${runId}/verification`, data).then(res => res.data)

export const getDroneRunValidation = (runId) =>
  api.get(`/drone-runs/${runId}/validation`).then(res => res.data)

export const getDroneRunThumbnails = (runId) =>
  api.get(`/drone-runs/${runId}/thumbnails`).then(res => res.data)

export const getDroneComposition = (ranchId) =>
  api.get('/drone-runs/composition', { params: { ranch_id: ranchId } }).then(res => res.data)

export const signOffFlightVerification = (runId, data = {}) =>
  api.post(`/drone-runs/${runId}/verification/sign-off`, data).then(res => res.data)

export const getPendingFlightVerifications = (ranchId) =>
  api.get('/drone-runs/verification/pending', { params: { ranch_id: ranchId } }).then(res => res.data)

// Cycle progress (S2)
export const getCycleProgress = (params) =>
  api.get('/cycle-progress', { params }).then(res => res.data)

// Instrument calibrations (S2)
export const getInstrumentCalibrations = (ranchId, params = {}) =>
  api.get('/instrument-calibrations', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getOverdueInstrumentCalibrations = (ranchId) =>
  api.get('/instrument-calibrations/overdue', { params: { ranch_id: ranchId } }).then(res => res.data)

export const createInstrumentCalibration = (data) =>
  api.post('/instrument-calibrations', data).then(res => res.data)

export const updateInstrumentCalibration = (id, data) =>
  api.patch(`/instrument-calibrations/${id}`, data).then(res => res.data)

// Field log entries (S2)
export const getFieldLogEntries = (ranchId, params = {}) =>
  api.get('/field-log', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const createFieldLogEntry = (data) =>
  api.post('/field-log', data).then(res => res.data)

export const updateFieldLogEntry = (id, data) =>
  api.patch(`/field-log/${id}`, data).then(res => res.data)

// -----------------------------------------------------------------------------
// VEGETATION POLYGONS (MRV — classifier label store)
// -----------------------------------------------------------------------------

export const getVegetationPolygons = (ranchId, params = {}) =>
  api.get('/vegetation-polygons', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getPolygonSummary = (ranchId, mappingVersion = null) =>
  api.get('/vegetation-polygons/summary', {
    params: { ranch_id: ranchId, ...(mappingVersion ? { mapping_version: mappingVersion } : {}) },
  }).then(res => res.data)

export const createVegetationPolygon = (data) =>
  api.post('/vegetation-polygons', data).then(res => res.data)

export const uploadGeoJSONPolygons = (data) =>
  api.post('/vegetation-polygons/upload-geojson', data).then(res => res.data)

export const updateVegetationPolygon = (id, data) =>
  api.patch(`/vegetation-polygons/${id}`, data).then(res => res.data)

export const deleteVegetationPolygon = (id) =>
  api.delete(`/vegetation-polygons/${id}`).then(res => res.data)

// -----------------------------------------------------------------------------
// CHAIN ANCHORS (MRV — evidence manifest + hash anchoring)
// -----------------------------------------------------------------------------

export const getChainAnchors = (ranchId, params = {}) =>
  api.get('/chain-anchors', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getChainAnchor = (anchorId) =>
  api.get(`/chain-anchors/${anchorId}`).then(res => res.data)

export const verifyChainAnchor = (anchorId) =>
  api.get(`/chain-anchors/${anchorId}/verify`).then(res => res.data)

export const downloadChainAnchorManifest = (anchorId) =>
  api.get(`/chain-anchors/${anchorId}/manifest`, { responseType: 'text' }).then(res => res.data)

export const generateChainAnchor = (ranchId, month = null) =>
  api.post('/chain-anchors/generate', {
    ranch_id: ranchId,
    ...(month ? { month } : {}),
  }).then(res => res.data)

// ============================================================================
// Pilot tracker (S1 of biochar pilot land monitoring plan, mig 092-094)
// ============================================================================

export const getPilotProjects = (ranchId) =>
  api.get('/pilot-projects/', { params: { ranch_id: ranchId } }).then(res => res.data)

export const getPilotProject = (pilotId) =>
  api.get(`/pilot-projects/${pilotId}`).then(res => res.data)

export const createPilotProject = (data) =>
  api.post('/pilot-projects/', data).then(res => res.data)

export const updatePilotProject = (pilotId, data) =>
  api.patch(`/pilot-projects/${pilotId}`, data).then(res => res.data)

export const getPilotMilestones = (pilotId) =>
  api.get(`/pilot-projects/${pilotId}/milestones`).then(res => res.data)

export const createPilotMilestone = (pilotId, data) =>
  api.post(`/pilot-projects/${pilotId}/milestones`, data).then(res => res.data)

export const updatePilotMilestone = (milestoneId, data) =>
  api.patch(`/pilot-milestones/${milestoneId}`, data).then(res => res.data)

export const getPilotEvidence = (milestoneId, includeDetached = false) =>
  api.get(`/pilot-milestones/${milestoneId}/evidence`, {
    params: { include_detached: includeDetached },
  }).then(res => res.data)

export const attachPilotEvidence = (milestoneId, data) =>
  api.post(`/pilot-milestones/${milestoneId}/evidence`, data).then(res => res.data)

export const detachPilotEvidence = (linkId, reason) =>
  api.delete(`/pilot-evidence-links/${linkId}`, { params: { reason } }).then(res => res.data)

export const getPilotAgreements = (pilotId) =>
  api.get(`/pilot-projects/${pilotId}/agreements`).then(res => res.data)

export const createPilotAgreement = (pilotId, data) =>
  api.post(`/pilot-projects/${pilotId}/agreements`, data).then(res => res.data)

// ============================================================================
// Field markers (S1, mig 094)
// ============================================================================

export const getFieldMarkers = (ranchId, params = {}) =>
  api.get('/field-markers/', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getFieldMarker = (markerId) =>
  api.get(`/field-markers/${markerId}`).then(res => res.data)

export const createFieldMarker = (data) =>
  api.post('/field-markers/', data).then(res => res.data)

export const updateFieldMarker = (markerId, data) =>
  api.patch(`/field-markers/${markerId}`, data).then(res => res.data)

export const deleteFieldMarker = (markerId) =>
  api.delete(`/field-markers/${markerId}`).then(res => res.data)

// ============================================================================
// Worker records (S1, mig 094)
// ============================================================================

export const getWorkers = (ranchId, params = {}) =>
  api.get('/workers/', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

export const getWorker = (workerId) =>
  api.get(`/workers/${workerId}`).then(res => res.data)

export const createWorker = (data) =>
  api.post('/workers/', data).then(res => res.data)

export const updateWorker = (workerId, data) =>
  api.patch(`/workers/${workerId}`, data).then(res => res.data)

export const deleteWorker = (workerId) =>
  api.delete(`/workers/${workerId}`).then(res => res.data)

// ============================================================================
// AI model registry (S3 of biochar pilot, mig 098)
// ============================================================================

export const getAIModels = (params = {}) =>
  api.get('/ai-models/', { params }).then(res => res.data)

export const getAIModel = (modelId) =>
  api.get(`/ai-models/${modelId}`).then(res => res.data)

export const registerAIModel = (data) =>
  api.post('/ai-models/', data).then(res => res.data)

export const updateAIModel = (modelId, data) =>
  api.patch(`/ai-models/${modelId}`, data).then(res => res.data)

export const deleteAIModel = (modelId) =>
  api.delete(`/ai-models/${modelId}`).then(res => res.data)

export const getAIModelEvaluations = (modelId, params = {}) =>
  api.get(`/ai-models/${modelId}/evaluations`, { params }).then(res => res.data)

export const createAIModelEvaluation = (modelId, data) =>
  api.post(`/ai-models/${modelId}/evaluations`, data).then(res => res.data)

// ============================================================================
// Lantana coverage report + spatial claims (S3 of biochar pilot, mig 099)
// ============================================================================

export const getLantanaCoverageReport = ({ ranch_id, on_date, source } = {}) =>
  api
    .get('/lantana-coverage-report/', { params: { ranch_id, on_date, source } })
    .then(res => res.data)

export const createLantanaSnapshot = (data) =>
  api.post('/lantana-coverage-report/snapshots', data).then(res => res.data)

export const getLantanaSnapshots = (params = {}) =>
  api.get('/lantana-coverage-report/snapshots', { params }).then(res => res.data)

export const getSpatialClaims = (params = {}) =>
  api.get('/spatial-claims/', { params }).then(res => res.data)

export const createSpatialClaim = (data) =>
  api.post('/spatial-claims/', data).then(res => res.data)

export const deleteSpatialClaim = (claimId, reason) =>
  api
    .delete(`/spatial-claims/${claimId}`, { params: { reason } })
    .then(res => res.data)

// --- Digital twin (mig 130): base/actual/best trajectories + register ------
export const getTwinTrajectories = (ranchId, metric = 'herd_liveweight_kg', months) =>
  api.get(`/twin/${ranchId}/trajectories`, { params: { metric, months } })
    .then(res => res.data)

export const getTwinAssumptions = (ranchId, { includeSuperseded = false } = {}) =>
  api.get(`/twin/${ranchId}/assumptions`,
          { params: { include_superseded: includeSuperseded } })
    .then(res => res.data)

export const getTwinStatus = (ranchId) =>
  api.get(`/twin/${ranchId}/status`).then(res => res.data)

// Phase 5 value waterfall — finance lines are owner-gated server-side
// (None, never 403), so this is safe to call for any ranch member.
export const getTwinValue = (ranchId) =>
  api.get(`/twin/${ranchId}/value`).then(res => res.data)

// Quarterly measured-uplift statement (the Pay-on-Proof fee base /
// fairness referee). Owner-gated server-side: non-owners get
// {available:false, finance_visible:false}, never a 403.
export const getTwinUplift = (ranchId) =>
  api.get(`/twin/${ranchId}/uplift`).then(res => res.data)

// Fee statement: the three payments (wage / calf share / better price) in
// farmer units, with the fairness ceiling. Owner-gated like the above.
export const getTwinFeeStatement = (ranchId) =>
  api.get(`/twin/${ranchId}/fee-statement`).then(res => res.data)

// Phase 6 cross-farm view — membership-based server-side (sysadmin and
// assurance officer see all ranches; everyone else their own).
export const getTwinFleet = ({ signal } = {}) =>
  api.get('/twin/fleet', { signal }).then(res => res.data)

// --- Pasture pins (mig 124): tasks, guided captures, incursions ------------
export const getPinTasks = (ranchId, { targetDate, status } = {}) =>
  api.get('/pasture-pins/tasks', {
    params: { ranch_id: ranchId, target_date: targetDate, status },
  }).then(res => res.data)

export const createPinObservation = (data) =>
  queuedMutation(api, {
    method: 'POST', url: '/pasture-pins/observations', data,
    label: 'Pasture pin capture',
  })

export const reportPinIncursion = (data) =>
  queuedMutation(api, {
    method: 'POST', url: '/pasture-pins/incursions', data,
    label: 'Incursion report',
  })

export const getPinSeries = (paddockId, ranchId, days = 90) =>
  api.get(`/pasture-pins/paddocks/${paddockId}/series`,
          { params: { ranch_id: ranchId, days } })
    .then(res => res.data)

// Weekly pin-protocol compliance (Phase 5, SHADOW MODE — informational
// until the payment plan's consensus gate consumes it). Default week =
// the previous complete Kampala week.
export const getPinCompliance = (ranchId, weekStart) =>
  api.get('/pasture-pins/compliance',
          { params: { ranch_id: ranchId, week_start: weekStart } })
    .then(res => res.data)

// Upload one queued pin photo (photoQueue.js item shape). Slot intent
// travels in the media context (pin_photo_a|b|c) so server-side adoption
// stays slot-faithful whichever order photos and JSON arrive.
export const uploadPinPhoto = ({ ranchId, clientObsId, slot, blob, filename }) => {
  const formData = new FormData()
  formData.append('file', blob, filename || `${clientObsId}-${slot}.jpg`)
  formData.append('context', `pin_photo_${slot}`)
  formData.append('ranch_id', ranchId)
  formData.append('entity_type', 'pin_observation')
  formData.append('entity_id', clientObsId)
  return api.post('/field-photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data)
}

export const recomputeTwin = (ranchId) =>
  api.post(`/twin/${ranchId}/recompute`).then(res => res.data)

export default api

// =============================================================================
// SmartKraal Data API mirror (mig 135) — served from our own tables, never
// from the SmartKraal API in the request path.
// =============================================================================
export const getSmartKraalHealth = (ranchId, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/health`, { signal }).then(res => res.data)

export const getSmartKraalPromotionPreview = (ranchId, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/promotion-preview`, { signal }).then(res => res.data)

export const getSmartKraalHerdSummary = (ranchId, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/herd-summary`, { signal }).then(res => res.data)

export const getSmartKraalWeighings = (animalId, { signal } = {}) =>
  api.get(`/smartkraal/animals/${animalId}/weighings`, { signal }).then(res => res.data)

// Which SmartKraal farm id a ranch owns (smartkraal.farms, mig 137). System
// admin only; the key itself never travels here, only the env var's name.
export const getSmartKraalFarm = (ranchId, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/farm`, { signal }).then(res => res.data)

export const updateSmartKraalFarm = (ranchId, data) =>
  api.put(`/smartkraal/${ranchId}/farm`, data).then(res => res.data)

// Registration at the crush (mig 136, D15 Phase 2): the tag queue, binding,
// new animals from the SmartKraal record, retiring a binding. Binds are not
// queued offline on purpose: each needs the server's conflict checks (tag or
// animal already bound) answered on the spot.
export const getSmartKraalRegistrationCounts = (ranchId, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/registration/counts`, { signal }).then(res => res.data)

export const getSmartKraalRegistrationQueue = (ranchId, params = {}, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/registration/queue`, { params, signal }).then(res => res.data)

export const getSmartKraalTagRegistry = (ranchId, pIdtag, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/registration/tag-registry/${pIdtag}`, { signal }).then(res => res.data)

// The caller's own animals at the crush (Phase 4a): counts per binding status and
// the animals that need a glance, scoped by the explicit owner-segment account link.
export const getSmartKraalRegistrationMine = (ranchId, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/registration/mine`, { signal }).then(res => res.data)

export const bindSmartKraalTag = (ranchId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/bind`, data).then(res => res.data)

export const bindSmartKraalTagByCrushEntry = (ranchId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/bind-by-crush-entry`, data).then(res => res.data)

export const createAnimalFromSmartKraalTag = (ranchId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/create-animal`, data).then(res => res.data)

// Phase 4b-2: the Profiles tab after a census / tagging day (crush-registered animals
// with blank fields; fill-blanks-only bulk save) and the explicit placeholder -> EID swap.
export const getSmartKraalIncompleteProfiles = (ranchId, params = {}, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/registration/incomplete-profiles`, { params, signal }).then(res => res.data)

export const completeSmartKraalProfiles = (ranchId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/complete-profiles`, data).then(res => res.data)

export const assignSmartKraalEid = (ranchId, animalId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/animals/${animalId}/assign-eid`, data).then(res => res.data)

// Phase 4c: the day-90 acceptance summary as of a date (gates, coverage, review,
// weights, profiles, weekly timeline, people, context). Advisory; the Onboarding tab.
export const getSmartKraalRegistrationAcceptance = (ranchId, params = {}, { signal } = {}) =>
  api.get(`/smartkraal/${ranchId}/registration/acceptance`, { params, signal }).then(res => res.data)

export const retireSmartKraalTagLink = (ranchId, linkId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/links/${linkId}/retire`, data).then(res => res.data)

// Review loop (D15 Phase 3): the assurance officer / system admin confirm a
// binding (acknowledging the checks failing at that moment) or send it back
// with a note (promotion suspended, the tag's weights retracted until re-bound).
export const confirmSmartKraalTagLink = (ranchId, linkId, data = {}) =>
  api.post(`/smartkraal/${ranchId}/registration/links/${linkId}/confirm`, data).then(res => res.data)

export const sendBackSmartKraalTagLink = (ranchId, linkId, data) =>
  api.post(`/smartkraal/${ranchId}/registration/links/${linkId}/send-back`, data).then(res => res.data)

export const smartKraalTagLinksCsvUrl = (ranchId) => `/api/v1/smartkraal/${ranchId}/tag-links.csv`
